import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { createPortal } from 'react-dom'

import { MeetingProviderAccountIntegrationConfig } from '../../../../models/Account'

import { HeaderContext } from '../../../../views/LayoutWithLeftSidebar/HeaderContext'

import { IntegrationServiceId } from '../../../../common/integrations'
import { parseMetadata } from '../../../../common/util'

import { selectRoomSession, selectRoomSessionIsLoading, startRoomSessionPolling, stopRoomSessionPolling } from '../../../../store/meetingSummary/roomSession'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { selectPreparationStopOnly } from '../../../../store/meetingSummary/display'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { useGetMeQuery } from '../../../../store/user/user'

import { MeetingSummaryToast } from '../MeetingSummary/Toast/MeetingSummaryToast'

import { MeetingPreparationInitialization } from './Initialization/MeetingPreparationInitialization'
import { MeetingPreparationHeader } from './Header/MeetingPreparationHeader'
import { MeetingPreparationPoller } from './Poller/MeetingPreparationPoller'
import { Participants } from './Participants/Participants'
import { Interactions } from './Interactions/Interactions'
import { MeetingInfo } from './MeetingInfo/MeetingInfo'
import { PhoneControls } from './Phone/PhoneControls'
import { InPersonMeeting } from './InPersonMeeting'
import { InPersonComplianceMessage } from './InPersonComplianceMessage'
import { ZoomOBFWarningRow } from '../../../../components/ZoomOBFWarning/ZoomOBFWarningRow'

export interface MeetingPreparationProps {
  onSessionFinished: () => void
}

export const MeetingPreparation: React.FC<MeetingPreparationProps> = ({ onSessionFinished }) => {
  const dispatch = useAppDispatch()

  const { portalRef } = useContext(HeaderContext)
  const [headerContainer, setHeaderContainer] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  useEffect(() => {
    dispatch(startRoomSessionPolling())
    return () => {
      dispatch(stopRoomSessionPolling())
    }
  }, [dispatch])

  return (
    <>
      {headerContainer ? createPortal(<MeetingPreparationHeader />, headerContainer) : null}
      <MeetingPreparationInitialization />
      <MeetingPreparationPoller />
      <MeetingPreparationContent onSessionFinished={onSessionFinished} />
      <MeetingSummaryToast />
    </>
  )
}

interface MeetingPreparationContentProps {
  onSessionFinished: () => void
}

const MeetingPreparationContent: React.FC<MeetingPreparationContentProps> = ({ onSessionFinished }) => {
  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const roomSessionIsLoading = useAppSelector(selectRoomSessionIsLoading)
  const roomSession = useAppSelector(selectRoomSession)
  const { source, provider = null } = parseMetadata(roomSession?.metadata ?? '')
  const stopOnly = useAppSelector(selectPreparationStopOnly)

  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { data: userData } = useGetMeQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const [interactionsLoading, setInteractionsLoading] = useState(false)
  const [linkedServiceId, setLinkedServiceId] = useState<string | null>(null)

  const hasPhoneIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.PHONE && i.state === 'live'))
  }, [userData])

  const phoneConfig = useMemo(() => {
    if (!account?.data?.effectiveConfig?.integrations) {
      return null
    }
    return (
      (account.data.effectiveConfig.integrations.find(
        (integration) => integration.type === 'meeting_provider' && integration.serviceId === 'phone',
      ) as MeetingProviderAccountIntegrationConfig) ?? null
    )
  }, [account])

  useEffect(() => {
    if (roomSession && roomSession.finishedAt && roomSession.finishedAt <= new Date().toISOString()) {
      onSessionFinished()
    }
  }, [onSessionFinished, roomSession])

  return (
    <div className='full-size-content'>
      <Container className='mw-100 p-0 d-flex flex-column zocks-gap-2'>
        <MeetingInfo />
        {!roomSessionIsLoading && !!roomSession && (!source || source === 'voice-memo') && roomSession.roomCreatedBy?.id === currentUserId ? (
          <>
            <InPersonMeeting accountId={accountId} roomSession={roomSession} />
            <InPersonComplianceMessage />
          </>
        ) : null}
        <ZoomOBFWarningRow rowProps={{ className: 'mx-0' }} />
        {!roomSessionIsLoading &&
        !!roomSession &&
        !roomSession.isActive &&
        source === 'phone' &&
        provider !== IntegrationServiceId.RINGCENTRAL &&
        roomSession.roomCreatedBy?.id === currentUserId &&
        (phoneConfig?.sharedPhoneLineEnabled || hasPhoneIntegration) ? (
          <Row className='d-flex justify-content-start align-items-start'>
            <Col className='phone-controls-container'>
              <PhoneControls hasPhoneIntegration={hasPhoneIntegration} phoneConfig={phoneConfig} />
            </Col>
          </Row>
        ) : null}
        {!stopOnly ? (
          <>
            <Participants interactionsLoading={interactionsLoading} onContactUpdated={(serviceId) => setLinkedServiceId(serviceId)} />
            {roomSession ? (
              <Interactions
                roomSession={roomSession}
                onLoadingChange={setInteractionsLoading}
                contactUpdatedServiceId={linkedServiceId}
                onDismissContactUpdate={() => setLinkedServiceId(null)}
              />
            ) : null}
          </>
        ) : null}
      </Container>
    </div>
  )
}
